// DisplayCarousel.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const DisplayCarousel = () => {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(null);

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

    // Fetch files from Laravel backend
    const fetchFiles = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE_URL}/api/filelist`);
            setFiles(response.data);
            setError('');
        } catch (err) {
            setError('Failed to fetch files. Make sure the server is running.');
            console.error('Error fetching files:', err);
        } finally {
            setLoading(false);
        }
    };

    // Handle file upload
    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file size (5MB)
        if (file.size > 5 * 1024 * 1024) {
            alert('File size must be less than 5MB');
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        try {
            setUploading(true);
            await axios.post(`${API_BASE_URL}/api/upload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            
            // Refresh the file list after successful upload
            await fetchFiles();
            showToast('File uploaded successfully!', 'success');
            
            // Reset file input
            event.target.value = '';
        } catch (err) {
            console.error('Error uploading file:', err);
            showToast('Failed to upload file', 'error');
        } finally {
            setUploading(false);
        }
    };

    // Handle file deletion
    const handleDeleteFile = async (fileUrl) => {
        const filename = getFileName(fileUrl);
        
        if (!window.confirm(`Are you sure you want to delete "${filename}"?`)) {
            return;
        }

        try {
            setDeleting(filename);
            await axios.delete(`${API_BASE_URL}/api/delete-file`, {
                data: { filename: filename }
            });
            
            // Remove the file from the state immediately
            setFiles(files.filter(file => file !== fileUrl));
            showToast('File deleted successfully!', 'success');
        } catch (err) {
            console.error('Error deleting file:', err);
            showToast('Failed to delete file', 'error');
        } finally {
            setDeleting(null);
        }
    };

    // Toast notification function
    const showToast = (message, type = 'info') => {
        const toast = document.createElement('div');
        toast.className = `alert alert-${type} fixed top-4 right-4 z-50 max-w-sm shadow-lg`;
        toast.innerHTML = `
            <div class="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="${
                        type === 'success' ? 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"' :
                        type === 'error' ? 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"' :
                        'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                    }" />
                </svg>
                <span>${message}</span>
            </div>
        `;
        document.body.appendChild(toast);
        setTimeout(() => {
            if (document.body.contains(toast)) {
                document.body.removeChild(toast);
            }
        }, 3000);
    };

    useEffect(() => {
        fetchFiles();
    }, []);

    // Extract filename from URL
    const getFileName = (url) => {
        return url.split('/').pop();
    };

    // Get file type for icon display
    const getFileType = (url) => {
        const filename = getFileName(url);
        const extension = filename.split('.').pop().toLowerCase();
        return extension;
    };

    // Get file icon based on extension
    const getFileIcon = (url) => {
        const fileType = getFileType(url);
        const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
        const documentTypes = ['pdf', 'doc', 'docx', 'txt'];
        const archiveTypes = ['zip', 'rar', '7z'];

        if (imageTypes.includes(fileType)) {
            return '🖼️';
        } else if (documentTypes.includes(fileType)) {
            return '📄';
        } else if (archiveTypes.includes(fileType)) {
            return '📦';
        }
        return '📁';
    };

    // Check if file is an image
    const isImage = (url) => {
        const fileType = getFileType(url);
        const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
        return imageTypes.includes(fileType);
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-64">
                <span className="loading loading-spinner loading-lg text-primary"></span>
                <p className="mt-4 text-lg">Loading files...</p>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-4 max-w-7xl">
            {/* Header */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-4xl font-bold text-gray-800">Hero Image Gallery</h1>
                    <p className="text-gray-600 mt-2">Manage Hero/Carousel images</p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3">
                    {/* Upload Button */}
                    <label className={`btn btn-primary ${uploading ? 'loading' : ''}`}>
                        {uploading ? 'Uploading...' : 'Upload File'}
                        <input 
                            type="file" 
                            className="hidden" 
                            onChange={handleFileUpload}
                            disabled={uploading}
                        />
                    </label>
                    
                    <button 
                        className="btn btn-outline btn-secondary"
                        onClick={fetchFiles}
                        disabled={loading}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                        </svg>
                        Refresh
                    </button>
                </div>
            </div>

            {/* Error Display */}
            {error && (
                <div className="alert alert-error mb-6">
                    <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{error}</span>
                </div>
            )}

            {/* Files Grid */}
            {files.length === 0 ? (
                <div className="text-center py-16 bg-base-200 rounded-2xl">
                    <div className="text-8xl mb-6">📁</div>
                    <h3 className="text-2xl font-semibold text-gray-700 mb-3">No files uploaded yet</h3>
                    <p className="text-gray-500 mb-6">Upload your first file to get started</p>
                    <label className="btn btn-primary btn-lg">
                        Upload First File
                        <input 
                            type="file" 
                            className="hidden" 
                            onChange={handleFileUpload}
                        />
                    </label>
                </div>
            ) : (
                <>
                    {/* Stats */}
                    <div className="stats shadow mb-6">
                        <div className="stat">
                            <div className="stat-figure text-primary">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="inline-block w-8 h-8 stroke-current">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                                </svg>
                            </div>
                            <div className="stat-title">Total Files</div>
                            <div className="stat-value text-primary">{files.length}</div>
                        </div>
                    </div>

                    {/* Files Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {files.map((fileUrl, index) => (
                            <div key={index} className="card bg-base-100 shadow-xl hover:shadow-2xl transition-all duration-300 border border-base-300 group">
                                <figure className="aspect-video relative">
                                    {isImage(fileUrl) ? (
                                        <img 
                                            src={fileUrl} 
                                            alt={getFileName(fileUrl)}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                    ) : (
                                        <div className="flex items-center justify-center w-full h-full bg-gradient-to-br from-primary to-secondary">
                                            <span className="text-6xl text-white">
                                                {getFileIcon(fileUrl)}
                                            </span>
                                        </div>
                                    )}
                                    <div className="absolute top-2 right-2 badge badge-accent badge-sm">
                                        {getFileType(fileUrl).toUpperCase()}
                                    </div>
                                    
                                    {/* Delete Button Overlay */}
                                    <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        <button 
                                            className="btn btn-error btn-sm btn-circle"
                                            onClick={() => handleDeleteFile(fileUrl)}
                                            disabled={deleting === getFileName(fileUrl)}
                                            title="Delete file"
                                        >
                                            {deleting === getFileName(fileUrl) ? (
                                                <span className="loading loading-spinner loading-xs"></span>
                                            ) : (
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                </figure>
                                
                                <div className="card-body p-4">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex-1 min-w-0 mr-2">
                                            <h3 className="card-title text-sm font-semibold truncate mb-1">
                                                {getFileName(fileUrl)}
                                            </h3>
                                            <p className="text-xs text-gray-500">
                                                Added recently
                                            </p>
                                        </div>
                                        <div className="text-2xl flex-shrink-0">
                                            {getFileIcon(fileUrl)}
                                        </div>
                                    </div>
                                    
                                    <div className="card-actions justify-between items-center">
                                        <a 
                                            href={fileUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-primary btn-sm flex-1 mr-2"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                            </svg>
                                            View
                                        </a>
                                        <button 
                                            className="btn btn-outline btn-sm"
                                            onClick={() => {
                                                navigator.clipboard.writeText(fileUrl);
                                                showToast('URL copied to clipboard!', 'success');
                                            }}
                                            title="Copy URL"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                                                <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default DisplayCarousel;